import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export type ExportFormat = "pdf" | "excel" | "csv" | "copy" | "print";

export interface ExportColumn {
  header: string;
  key: string;
}

export interface ExportOptions {
  filename: string;
  title?: string;
  columns: ExportColumn[];
  data: Record<string, unknown>[];
}

export interface ExportResult {
  success: boolean;
  message: string;
}

export async function exportTable(
  format: ExportFormat,
  options: ExportOptions
): Promise<ExportResult | undefined> {
  const { filename, title, columns, data } = options;

  switch (format) {
    case "pdf":
      exportToPDF(filename, title, columns, data);
      return { success: true, message: "PDF exported successfully" };
    case "excel":
      await exportToExcel(filename, columns, data);
      return { success: true, message: "Excel file exported successfully" };
    case "csv":
      exportToCSV(filename, columns, data);
      return { success: true, message: "CSV file exported successfully" };
    case "copy":
      return await copyToClipboard(columns, data);
    case "print":
      return printTable(title, columns, data);
  }
}

function exportToPDF(
  filename: string,
  title: string | undefined,
  columns: ExportColumn[],
  data: Record<string, unknown>[]
): void {
  const doc = new jsPDF();

  if (title) {
    doc.setFontSize(16);
    doc.text(title, 14, 15);
  }

  autoTable(doc, {
    startY: title ? 25 : 15,
    head: [columns.map((col) => col.header)],
    body: data.map((row) =>
      columns.map((col) => {
        const value = row[col.key];
        if (value === null || value === undefined) return "";
        if (typeof value === "object") return JSON.stringify(value);
        return String(value as string);
      })
    ),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [28, 49, 68] },
  });

  doc.save(`${filename}.pdf`);
}

async function exportToExcel(
  filename: string,
  columns: ExportColumn[],
  data: Record<string, unknown>[]
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Data");

  worksheet.columns = columns.map((col) => ({
    header: col.header,
    key: col.key,
    width: 15,
  }));

  data.forEach((row) => {
    const rowData: Record<string, unknown> = {};
    columns.forEach((col) => {
      rowData[col.key] = row[col.key];
    });
    worksheet.addRow(rowData);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `${filename}.xlsx`);
}

function exportToCSV(
  filename: string,
  columns: ExportColumn[],
  data: Record<string, unknown>[]
): void {
  const headers = columns.map((col) => col.header).join(",");
  const rows = data.map((row) =>
    columns
      .map((col) => {
        const value = row[col.key];
        let stringValue = "";
        if (value !== null && value !== undefined) {
          stringValue = typeof value === "object" ? JSON.stringify(value) : String(value as string);
        }
        return `"${stringValue.replace(/"/g, '""')}"`;
      })
      .join(",")
  );

  const csv = [headers, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

  saveAs(blob, `${filename}.csv`);
}

async function copyToClipboard(
  columns: ExportColumn[],
  data: Record<string, unknown>[]
): Promise<{ success: boolean; message: string }> {
  const headers = columns.map((col) => col.header).join("\t");
  const rows = data.map((row) =>
    columns
      .map((col) => {
        const value = row[col.key];
        if (value === null || value === undefined) return "";
        if (typeof value === "object") return JSON.stringify(value);
        return String(value as string);
      })
      .join("\t")
  );

  const text = [headers, ...rows].join("\n");

  try {
    await navigator.clipboard.writeText(text);
    return { success: true, message: "Table data copied to clipboard!" };
  } catch {
    return { success: false, message: "Failed to copy to clipboard" };
  }
}

function printTable(
  title: string | undefined,
  columns: ExportColumn[],
  data: Record<string, unknown>[]
): { success: boolean; message: string } {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    return { success: false, message: "Please allow popups to print" };
  }

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title ?? "Print"}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
          }
          h1 {
            color: #1c2024;
            margin-bottom: 20px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          th, td {
            border: 1px solid #e8e8ec;
            padding: 8px 12px;
            text-align: left;
          }
          th {
            background-color: #f9f9fb;
            font-weight: 600;
            color: #1c2024;
          }
          tr:hover {
            background-color: #f9f9fb;
          }
          @media print {
            body {
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        ${title ? `<h1>${title}</h1>` : ""}
        <table>
          <thead>
            <tr>
              ${columns.map((col) => `<th>${col.header}</th>`).join("")}
            </tr>
          </thead>
          <tbody>
            ${data
              .map(
                (row) => `
              <tr>
                ${columns
                  .map((col) => {
                    const value = row[col.key];
                    let stringValue = "";
                    if (value !== null && value !== undefined) {
                      stringValue = typeof value === "object" ? JSON.stringify(value) : String(value as string);
                    }
                    return `<td>${stringValue}</td>`;
                  })
                  .join("")}
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
  return { success: true, message: "Print dialog opened" };
}

